import Vue, { PropType } from "vue";
import * as cwxApi from "@/api/cwxApi";
import {
  IdeaListSortingType,
  IdeaListFilterType,
  IdeaListDataItemType,
  IdeaListFilterUiType,
  IIdeaDto,
  ISelectListItemDto,
  ISelectListEnumItemDto
} from "@/backendTypes/allTypes";

interface VuetifyPagination {
  descending: boolean;
  page: number;
  itemsPerPage: number;
  sortBy: string;
  totalItems: number;
}

interface ISortByItem {
  sortByItemId: string;
  sortingType: IdeaListSortingType;
  descending: boolean;
  text: string;
  icon: string;
}

interface ISelectedFilter {
  readonly text: string;
  readonly key: string;
  readonly type: IdeaListFilterType;

  //optional data. Which one is included depends on `type` property
  readonly id?: string;
  readonly spaceId?: number;
  readonly fromDate?: string;
  readonly toDate?: string;
}

interface IMultiselectFilter {
  name: string;
  type: IdeaListFilterType;
  allOptions: IMultiselectFilterOption[];
  selectedOptions: IMultiselectFilterOption[];
}

interface IMultiselectFilterOption {
  id: string;
  text: string;
  type: IdeaListFilterType;
  spaceId?: number;
}

type IIdeaDataItemViewModel =
  | {
      uiType: "single";
      type: IdeaListDataItemType.State;
      text: string;
      value: string;
      valueHexColor: string;
      icon: string;
    }
  | {
      uiType: "single";
      type: IdeaListDataItemType;
      text: string;
      value: string | number;
      icon: string;
    }
  | {
      uiType: "multi";
      type: IdeaListDataItemType;
      text: string;
      value: string[] | number[];
      icon: string;
    };

interface IIdeaViewModel {
  imageUrl: string | null;
  title: string;
  ideaId: number;
  dataItems: IIdeaDataItemViewModel[];
}

export default Vue.extend({
  name: "ItemListTiles",

  props: {
    title: String,
    spaceId: {
      type: Number as PropType<number | null>,
      required: false,
      default: null
    }
  },

  data() {
    return {
      ideas: [] as IIdeaViewModel[],
      totalIdeasCount: 0,

      searchQuery: "",

      selectedSortByItem: null as ISortByItem | null,
      sortByItems: null! as ISortByItem[],

      isShowFiltersOn: false,
      multiselectFilters: [] as IMultiselectFilter[],

      pagination: {
        itemsPerPage: 30
      } as VuetifyPagination,
      listSettings: null as cwxApi.IdeaListSettings | null,

      // state variables that are not used to rendering template
      loadingSetting: false,
      loadingIdeas: false //TODO: add some visuals for loading (Data Iterator doesn't have it build in like data-table)
    };
  },

  computed: {
    loading(): boolean {
      return this.loadingSetting || this.loadingIdeas;
    },
    selectedFiltersComputed(): ISelectedFilter[] {
      const sf = this.multiselectFilters.flatMap(f =>
        f.selectedOptions.map(o => {
          const key = [o.type, o.id, o.spaceId]
            .filter(k => k != null && k != undefined)
            .join("_");
          return {
            text: `${f.name}: ${o.text}`,
            key,
            type: o.type,
            spaceId: o.spaceId,
            id: o.id
          };
        })
      );

      return sf;
    },
    isFiltersButtonVisible(): boolean {
      if (!this.listSettings) {
        return false;
      }
      return this.listSettings.filters.length > 0;
    },
    isSortDropdownVisible(): boolean {
      if (!this.listSettings) {
        return false;
      }
      return this.listSettings.sortings.length > 0;
    }
  },

  async mounted() {
    console.log("mounted: start, get list settings");

    this.loadingSetting = true;
    try {
      this.listSettings = await cwxApi.getIdeaListSettings(this.spaceId);
    } finally {
      this.loadingSetting = false;
    }

    this.setDataFromFromListSettings(this.listSettings);

    console.log("mounted: Settings fetched.startFetchIdeas.");
    await this.fetchIdeas();
    console.log("mounted: fetching finished");
  },

  watch: {
    pagination: {
      async handler(
        newPagination: VuetifyPagination,
        oldPagination: VuetifyPagination
      ) {
        console.group("pagination watch handler");
        console.log("loading: " + this.loading);
        console.log(arguments);
        console.log(this.pagination);
        console.groupEnd();

        if (this.loading) {
          console.log("Loading in progress. Doing nothing");
          return;
        }

        console.log("start fetchIdeas from watcher");
        await this.fetchIdeas(false);
        if (oldPagination.itemsPerPage === newPagination.itemsPerPage) {
          window.scrollTo(0, 0);
        }
      },
      deep: true
    }
  },

  methods: {
    setDataFromFromListSettings(listSettings: cwxApi.IdeaListSettings): void {
      const setSortingsData = () => {
        const ascAndDescSortItems = this.listSettings!.sortings.map(s => {
          const sortByItemAscending = {
            sortingType: s.value,
            descending: false,
            text: s.text,
            icon: "icon-chevron-up",
            sortByItemId: `${s.value}_asc`
          };

          const sortByItemDescending = {
            sortingType: s.value,
            descending: true,
            text: s.text,
            icon: "icon-chevron-down",
            sortByItemId: `${s.value}_desc`
          };

          const result = [sortByItemAscending, sortByItemDescending];
          return result;
        });

        const sortByItems = Array.prototype.concat.apply(
          [],
          ascAndDescSortItems
        );
        this.sortByItems = sortByItems;
      };

      const setFiltersData = () => {
        const multiselectFilters = listSettings.filters
          .map(filter => {
            if (filter.uiType == IdeaListFilterUiType.SelectItems) {
              const multiselectFilter: IMultiselectFilter = {
                name: filter.text,
                type: filter.type,
                allOptions: filter.selectListItems.map(filterOption => ({
                  id: filterOption.id,
                  text: filterOption.text,
                  type: filter.type
                })),
                selectedOptions: []
              };
              return multiselectFilter;
            }

            if (filter.uiType === IdeaListFilterUiType.SelectItemsWithSpaceId) {
              const multiselectFilter: IMultiselectFilter = {
                name: filter.text,
                type: filter.type,
                allOptions: filter.selectListItemsWithSpaceIds.flatMap(
                  ({ spaceId, selectListItems }) => {
                    const allOptions = selectListItems.map(filterOption => ({
                      id: filterOption.id,
                      text: filterOption.text,
                      type: filter.type,
                      spaceId: spaceId
                    }));

                    return allOptions;
                  }
                ),
                selectedOptions: []
              };
              return multiselectFilter;
            }

            return null;
          })
          .filter(f => f !== null) as IMultiselectFilter[];

        this.multiselectFilters = multiselectFilters;
      };

      setSortingsData();
      setFiltersData();
    },

    async fetchIdeas(forceFirstPage = true): Promise<void> {
      console.log("fetchIdeas start");
      this.loadingIdeas = true;

      const spaceIds =
        this.spaceId != null
          ? [this.spaceId.toString()]
          : this.selectedFiltersComputed
              .filter(f => f.type === IdeaListFilterType.Space)
              .map(f => f.id!);

      const regionIds = this.selectedFiltersComputed
        .filter(f => f.type === IdeaListFilterType.Region)
        .map(f => f.id!);

      const departmentIds = this.selectedFiltersComputed
        .filter(f => f.type === IdeaListFilterType.Department)
        .map(f => f.id!);

      const groupIds = this.selectedFiltersComputed
        .filter(f => f.type === IdeaListFilterType.Group)
        .map(f => f.id!);

      const categoryIds = this.selectedFiltersComputed
        .filter(f => f.type === IdeaListFilterType.Category)
        .map(f => f.id!);

      const ideaTypeIds = this.selectedFiltersComputed
        .filter(f => f.type === IdeaListFilterType.IdeaType)
        .map(f => f.id!);

      const stateIds = this.selectedFiltersComputed
        .filter(f => f.type === IdeaListFilterType.State)
        .map(f => f.id!);

      const apiResult = await cwxApi.getIdeas({
        itemsPerPage: this.pagination.itemsPerPage,
        pageNumber: forceFirstPage ? 1 : this.pagination.page,
        searchPhrase: this.searchQuery,
        sortBy:
          this.selectedSortByItem != null
            ? this.selectedSortByItem.sortingType
            : null,
        sortDesc:
          this.selectedSortByItem != null
            ? this.selectedSortByItem.descending
            : false,
        regionIds,
        departmentIds,
        groupIds,
        spaceIds,
        categoryIds,
        ideaTypeIds,
        stateIds
      });

      let _getIdeaViewModel = (
        ideaDto: IIdeaDto,
        listSettings: cwxApi.IdeaListSettings
      ): IIdeaViewModel | null => {
        const settingsForSpace =
          listSettings.spaceSpecificSettings[ideaDto.spaceId];
        if (!settingsForSpace) {
          console.error(
            "Missing space settings for given idea. " +
              `This idea will not be shown (ideaId=${ideaDto.ideaId}, spaceId=${ideaDto.spaceId}}) ` +
              "This should not happen. There is a bug somewhere. " +
              "Api should return all active non-target spaces' settings and return ideas that should be from this spaces. "
          );
          return null;
        }

        const dataItemsSettingsForSpace = settingsForSpace.dataItems;
        const dataItemsTypes = dataItemsSettingsForSpace.map(di => di.value);

        const ideaTitlePart1 = dataItemsTypes.includes(IdeaListDataItemType.Id)
          ? ideaDto.ideaId
          : null;
        const ideaTitlePart2 = dataItemsTypes.includes(
          IdeaListDataItemType.Title
        )
          ? ideaDto.title
          : null;
        const ideaTitle = [ideaTitlePart1, ideaTitlePart2]
          .filter(p => p != null)
          .join(" ");

        const imageUrl = dataItemsTypes.includes(IdeaListDataItemType.Image)
          ? ideaDto.imageLink
          : null;

        const dataItemsWhereOrderMatter = dataItemsSettingsForSpace.filter(
          dataItem =>
            [
              IdeaListDataItemType.Title,
              IdeaListDataItemType.Id,
              IdeaListDataItemType.Image
            ].indexOf(dataItem.value) === -1
        );

        const dataItemViewModels = dataItemsWhereOrderMatter.map(dataItem =>
          _getDataItemViewModelForIdea(ideaDto, dataItem)
        );

        return {
          ideaId: ideaDto.ideaId,
          title: ideaTitle,
          imageUrl,
          dataItems: dataItemViewModels
        };
      };

      let _getDataItemViewModelForIdea = (
        ideaDto: IIdeaDto,
        dataItem: ISelectListEnumItemDto<IdeaListDataItemType>
      ): IIdeaDataItemViewModel => {
        if (
          dataItem.value === IdeaListDataItemType.Title ||
          dataItem.value === IdeaListDataItemType.Id ||
          dataItem.value === IdeaListDataItemType.Image
        ) {
          throw new Error(
            "Title and Image should be handled separately because they have fixed position in layout and the order of other data items is dynamic."
          );
        }

        if (dataItem.value === IdeaListDataItemType.CreationDate) {
          return {
            uiType: "single",
            icon: "icon-calendar",
            text: dataItem.text,
            value: ideaDto.creationDate,
            type: dataItem.value
          };
        }

        if (dataItem.value === IdeaListDataItemType.ModifiedDate) {
          return {
            uiType: "single",
            icon: "icon-calendar",
            text: dataItem.text,
            value: ideaDto.modifiedDate,
            type: dataItem.value
          };
        }

        if (dataItem.value === IdeaListDataItemType.Visits) {
          return {
            uiType: "single",
            icon: "icon-eye-open",
            text: dataItem.text,
            value: ideaDto.noOfVisits,
            type: dataItem.value
          };
        }

        if (dataItem.value === IdeaListDataItemType.Comments) {
          return {
            uiType: "single",
            icon: "icon-comments-alt",
            text: dataItem.text,
            value: ideaDto.noOfComments,
            type: dataItem.value
          };
        }

        if (dataItem.value === IdeaListDataItemType.Type) {
          return {
            uiType: "single",
            icon: "icon-tag",
            text: dataItem.text,
            value: ideaDto.ideaType,
            type: dataItem.value
          };
        }

        if (dataItem.value === IdeaListDataItemType.Category) {
          return {
            uiType: "single",
            icon: "icon-list",
            text: dataItem.text,
            value: ideaDto.category,
            type: dataItem.value
          };
        }

        if (dataItem.value === IdeaListDataItemType.State) {
          return {
            uiType: "single",
            icon: "icon-info",
            text: dataItem.text,
            value: ideaDto.state,
            valueHexColor: ideaDto.stateHexColor,
            type: dataItem.value
          };
        }

        if (dataItem.value === IdeaListDataItemType.Access) {
          return {
            uiType: "single",
            icon: "icon-lock",
            text: dataItem.text,
            value: ideaDto.accessLevel,
            type: dataItem.value
          };
        }

        if (dataItem.value === IdeaListDataItemType.Space) {
          return {
            uiType: "single",
            icon: "icon-globe",
            text: dataItem.text,
            value: ideaDto.spaceName,
            type: dataItem.value
          };
        }

        if (dataItem.value === IdeaListDataItemType.Region) {
          return {
            uiType: "multi",
            icon: "icon-globe",
            text: dataItem.text,
            value: ideaDto.regions,
            type: dataItem.value
          };
        }

        if (dataItem.value === IdeaListDataItemType.Group) {
          return {
            uiType: "single",
            icon: "icon-sitemap",
            text: dataItem.text,
            value: ideaDto.group,
            type: dataItem.value
          };
        }

        if (dataItem.value === IdeaListDataItemType.Department) {
          return {
            uiType: "single",
            icon: "icon-sitemap",
            text: dataItem.text,
            value: ideaDto.department,
            type: dataItem.value
          };
        }

        if (dataItem.value === IdeaListDataItemType.VotingScore) {
          return {
            uiType: "single",
            icon: "icon-star",
            text: dataItem.text,
            value: ideaDto.votingScore,
            type: dataItem.value
          };
        }

        //just a TS "hack" for exhaustive check (https://basarat.gitbooks.io/typescript/docs/types/discriminated-unions.html)
        const _exhaustiveCheck: never = dataItem.value;
        throw new Error(`This should not happen ${_exhaustiveCheck}`);
      };

      this.ideas = apiResult.ideas
        .map(ideaDto => _getIdeaViewModel(ideaDto, this.listSettings!))
        .filter(i => i !== null) as IIdeaViewModel[]; //workaround for TS inability to infer type guards (https://github.com/Microsoft/TypeScript/issues/16069)

      this.totalIdeasCount = apiResult.totalItemsCount;
      this.loadingIdeas = false;
      console.log("fetch ideas end");
    },

    goToFirstPage(): void {
      this.pagination.page = 1;
    },

    goToLastPage(): void {
      this.pagination.page = Math.ceil(
        this.pagination.totalItems / this.pagination.itemsPerPage
      );
    },

    sortByItemChanged(selectedSortByItem?: ISortByItem): void {
      console.log("sortByItemChanged", arguments);

      if (selectedSortByItem) {
        this.selectedSortByItem = selectedSortByItem;
      } else {
        this.selectedSortByItem = null;
      }

      this.goToFirstPage();

      this.fetchIdeas();
    },

    toggleFilters(): void {
      this.isShowFiltersOn = !this.isShowFiltersOn;
    },

    clearFilters(): void {
      for (const mf of this.multiselectFilters) {
        mf.selectedOptions.splice(0);
      }
    },

    filterResultsClicked(): void {
      console.log("filterResultsClicked:");
      this.goToFirstPage();
      this.fetchIdeas();
    },

    searchQueryClicked() {
      console.log("searchQueryClicked:");
      this.goToFirstPage();
      this.fetchIdeas();
    },

    removeSelectedFilter(index: number, filter: ISelectedFilter): void {
      console.log("removeSelectedFilter", arguments);
      const mfFilter = this.multiselectFilters.find(
        mf => mf.type === filter.type
      );

      if (mfFilter) {
        var indexToRemove = mfFilter.selectedOptions.findIndex(option => {
          return option.id === filter.id;
        });
        if (indexToRemove != -1) {
          mfFilter.selectedOptions.splice(indexToRemove, 1);
        }
      }
    }
  }
});
